/**
 * Taxonomia da Matriz de Referencia CAEd -- Matematica, Ensino Medio.
 *
 * Espelha o vocabulario dos catalogos que ja existem no workspace:
 *   - Catalogo_TaskModels_Matematica_EM_CAEd.md          (85 task-models, camada transversal)
 *   - Catalogo_TaskModels_Matematica_EM_CAEd_detalhado.md (637 task-models)
 *   - _habilidades_extraidas.json                         (93 habilidades)
 */

/** Codigo de habilidade da matriz: 'H1.1', 'H2.14', 'H3.30'... (93 no total). */
export type SkillCode = string & { readonly __skill: unique symbol };
export const skillCode = (s: string): SkillCode => s as SkillCode;

/**
 * Codigo de task-model.
 *
 * Dois espacos de nomes convivem:
 *   - 'H3.30.05'   -- task-model do catalogo detalhado (item de escolha, papel ou tela)
 *   - 'H1.15.K01'  -- task-model de CONSTRUCAO (K), que so existe porque o meio digital
 *                     permite construir. O catalogo atual nao tem nenhum: ele foi escrito
 *                     para multipla escolha em papel, onde construir e impossivel.
 *                     O prefixo da habilidade preserva a rastreabilidade ate a matriz.
 */
export type TaskModelCode = string & { readonly __taskModel: unique symbol };
export const taskModelCode = (s: string): TaskModelCode => s as TaskModelCode;

/** Eixos cognitivos da matriz. */
export type CognitiveAxis = 'I' | 'II' | 'III';

export const AXIS_LABEL: Readonly<Record<CognitiveAxis, string>> = {
  I: 'Dominar linguagens e compreender fenômenos',
  II: 'Enfrentar situações-problema',
  III: 'Construir argumentação e elaborar propostas',
};

/** Competencias tematicas (6). */
export type Competency = 1 | 2 | 3 | 4 | 5 | 6;

export const COMPETENCY_LABEL: Readonly<Record<Competency, string>> = {
  1: 'Numérico-aritmética',
  2: 'Algébrico-funcional',
  3: 'Geométrica',
  4: 'Grandezas e medidas',
  5: 'Probabilidade e combinatória',
  6: 'Estatística',
};

export type Ano = 1 | 2 | 3;

export interface SkillMeta {
  readonly codigo: SkillCode;
  readonly ano: Ano;
  readonly competencia: Competency;
  readonly eixo: CognitiveAxis;
  readonly verbo: string;
  readonly descricao: string;
}

/**
 * Estruturas de item E1-E10 (secao 2.4 do catalogo de sintese).
 * Multiplicam cada task-model: o mesmo conteudo em formatos diferentes.
 */
export type AltStructure =
  | 'E1' // pergunta direta + 5 valores numericos
  | 'E2' // enunciado com suporte unico
  | 'E3' // dois suportes a integrar
  | 'E4' // completar lacuna
  | 'E5' // alternativas = expressoes / equacoes
  | 'E6' // alternativas = graficos / figuras
  | 'E7' // alternativas = afirmacoes / julgamentos
  | 'E8' // alternativas = procedimentos / passos
  | 'E9' // enunciado com dado irrelevante
  | 'E10'; /** Nivel de dificuldade declarado pelo autor (a engenharia da secao 2.3). */
export type DeclaredDifficulty = 'facil' | 'media' | 'dificil';

/** Identificador de alternativa. Fica aqui (modulo sem dependencias) para evitar ciclos. */
export type ChoiceId = 'A' | 'B' | 'C' | 'D' | 'E';

export const CHOICE_IDS: readonly ChoiceId[] = ['A', 'B', 'C', 'D', 'E'];
